import React, { useEffect, useMemo } from 'react';
import cx from 'classnames';
import {
  useTable, useSortBy, usePagination,
} from 'react-table';

import { getUniqueKey, shortize } from 'app/utils/helpers';
import { TEZOS_NODES_URL, TZKT_EXPLORER_URL } from 'app/utils/defaults';
import Link from 'app/components/common/Link';
import Button from 'app/components/ui/Button';
import { TransactionSelectItemsEnum } from 'app/components/tables/TransactionsTable/select';
import Preloader from 'app/components/common/Preloader';
import TFooter from './components/TFooter';

import s from './Table.module.sass';

type TablePropsT = {
  theme?: keyof typeof themeClass
  className?: string
  columns: any
  data: any
  fieldsToSorting?: string[]
  pageSize?: number
  manualPagination?: boolean
  setOffset?: (arg: number) => void
  pageCount?: number
  activeItem?: TransactionSelectItemsEnum
  loading?: boolean
};

const themeClass = {
  primary: s.primary,
  secondary: s.secondary,
  transactions: s.transactions,
  historyOfVote: s.historyOfVote,
  topPairs: s.topPairs,
  pairsAndBakers: s.pairsAndBakers,
  informationOfAChosenPair: s.informationOfAChosenPair,
  accounts: s.accounts,
  bakingRewards: s.bakingRewards,
  rewardsHistory: s.rewardsHistory,
  topLiquidityPositions: s.topLiquidityPositions,
  pairs: s.pairs,
};

const Table: React.FC<TablePropsT> = ({
  theme = 'primary',
  className,
  columns,
  data,
  fieldsToSorting,
  pageSize,
  // nameOfTable,
  manualPagination = true,
  setOffset,
  pageCount = 0,
  activeItem,
  loading,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    // pagination
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    gotoPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: pageSize || 10,
      },
      pageCount: Math.ceil(pageCount / (pageSize || 10)),
      manualPagination,
      disableSortRemove: true,
      autoResetPage: false,
      disableSortBy: true,
    },
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    if (setOffset) {
      const offset = pageIndex === 0 ? pageIndex : pageIndex * (pageSize || 10);
      setOffset(offset);
    }
  }, [pageIndex, pageSize, setOffset]);

  useEffect(() => {
    if (activeItem) {
      gotoPage(0);
    }
  }, [activeItem, gotoPage]);

  const links = useMemo(() => [
    'pairAddress',
    'token2Address',
    'addressOfBaker',
    'addressOfVoter',
    'transactionHash',
    'address',
    'accountsName',
    'accountsNumber',
    'account',
    'accountsAddress',
  ], []);

  const compoundClassName = cx(
    s.root,
    themeClass[theme],
    className,
  );

  const isShowPagination = Math.ceil(pageCount / (pageSize || 10)) > 1;

  return (
    <>
      <div className={compoundClassName}>
        <div className={s.wrapper}>
          <div className={s.innerWrapper}>
            <table {...getTableProps()}>
              <thead>
                {
              headerGroups.map((headerGroup) => (
                <tr key={getUniqueKey()}>
                  <th className={cx(s.row)}>
                    {headerGroup.headers.map((column) => {
                      const { id } = column;

                      if (
                        fieldsToSorting
                          && fieldsToSorting.length
                          && fieldsToSorting.includes(id)
                      ) {
                        return (
                          <div
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            key={getUniqueKey()}
                            className={cx(s.cell)}
                          >
                            <Button
                              className={cx(s.sortingButton)}
                              sizeT="medium"
                              theme="quinary"
                              sortType={column.isSortedDesc}
                              isSorted={column.isSorted}
                            >
                              {column.render('Header')}
                            </Button>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={getUniqueKey()}
                          className={cx(s.cell)}
                        >
                          {column.render('Header')}
                        </div>
                      );
                    })}
                  </th>
                </tr>
              ))
            }
              </thead>
              {data.length === 0 || loading ? (
                <div className={s.preloaderWrapper}>
                  <Preloader style={{ minHeight: 657 }} className={s.preloader} />
                </div>
              ) : (
                <tbody {...getTableBodyProps()}>
                  {
                    page.map((row) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()} key={getUniqueKey()}>
                          <td className={cx(s.row)}>
                            {
                              row.cells.map((cell) => {
                                const { id } = cell.column;

                                if (links.includes(id)) {
                                  let url = TZKT_EXPLORER_URL;

                                  if (id === 'addressOfBaker') {
                                    url = TEZOS_NODES_URL;
                                  }

                                  const noneShortizeAccountAddress = ['accountsAddress', 'accountsNumber'].includes(id);

                                  if (loading) {
                                    return (
                                      <div
                                        {...cell.getCellProps()}
                                        key={getUniqueKey()}
                                        className={cx(s.cell, s.blue)}
                                      >
                                        ...
                                      </div>
                                    );
                                  }

                                  return (
                                    <div
                                      {...cell.getCellProps()}
                                      key={getUniqueKey()}
                                      className={cx(s.cell, s.link)}
                                    >
                                      <a href={`${url}/${cell.value}`} target="_blank" rel="noreferrer">
                                        {noneShortizeAccountAddress
                                          ? cell.value
                                          : shortize(cell.value)}
                                      </a>
                                      <Link value={cell.value} className={cx(s.svgLink)} />
                                    </div>
                                  );
                                }

                                return (
                                  <div
                                    {...cell.getCellProps()}
                                    key={getUniqueKey()}
                                    className={cx(s.cell)}
                                  >
                                    {cell.render('Cell')}
                                  </div>
                                );
                              })
                            }
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              )}
            </table>
            <div className={cx(
              s.footer,
              s.desktop,
              { [s.smallFooter]: !isShowPagination && pageCount !== 0 },
            )}
            >
              <TFooter
                isShowPagination={isShowPagination}
                previousPage={previousPage}
                canPreviousPage={canPreviousPage}
                pageIndex={pageIndex}
                pageOptions={pageOptions}
                nextPage={nextPage}
                canNextPage={canNextPage}
              />
            </div>
          </div>
        </div>
      </div>
      <div className={cx(
        s.footer,
        s.mobileFooter,
        { [s.smallFooter]: !isShowPagination && pageCount !== 0 },
      )}
      >
        <TFooter
          isShowPagination={isShowPagination}
          previousPage={previousPage}
          canPreviousPage={canPreviousPage}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          nextPage={nextPage}
          canNextPage={canNextPage}
        />
      </div>
    </>
  );
};

export default Table;
